/**
 * Pure helpers for organization websites (docs/Decisions.md D43). No React,
 * no DOM: the builder, the preview and the public renderer share these, and
 * frontend/src/lib/sites.test.js covers them.
 *
 * The server (src/services/sites/palette.js) is the authority on palettes:
 * it validates, adjusts and computes the tokens. The contrast maths here
 * mirrors it only so the builder can show live feedback while someone picks
 * colours, before they save.
 */

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

export const PAGE_ORDER = ['home', 'about', 'events', 'contact', 'donate'];

export const AA_TEXT = 4.5;

export function isHex(value) {
  return typeof value === 'string' && HEX_RE.test(value);
}

/** WCAG 2 relative luminance of a #RRGGBB colour. */
export function relativeLuminance(hex) {
  const channels = [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16) / 255);
  const [r, g, b] = channels.map(s => (s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG 2 contrast ratio, 1 to 21; null when either colour is not #RRGGBB. */
export function contrastRatio(a, b) {
  if (!isHex(a) || !isHex(b)) return null;
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Live feedback for a custom palette, before the server computes on-colours:
 * the pairings a person chooses directly. Fills (accent, band) always get a
 * readable label computed by the server, so they are reported as such.
 */
export function contrastFeedback(colors) {
  const c = colors || {};
  const pair = (label, fg, bg) => {
    const ratio = contrastRatio(fg, bg);
    return {
      label,
      ratio: ratio === null ? null : Math.round(ratio * 100) / 100,
      pass: ratio !== null && ratio >= AA_TEXT,
    };
  };
  return [
    pair('Text on the page', c.ink, c.canvas),
    pair('Text on cards', c.ink, c.surface),
    pair('Links on cards', c.accent, c.surface),
  ];
}

/**
 * Approximate tokens for previewing an unsaved custom palette. The saved
 * palette uses the server's tokens (which also adjust weak text colours);
 * this only picks a readable label for each fill so the preview is honest.
 * The two label candidates come from the colours the person entered (their
 * ink and their surface), so no colour is invented here.
 */
export function previewTokens(colors) {
  const c = colors || {};
  if (![c.canvas, c.surface, c.ink, c.accent, c.band].every(isHex)) return null;
  const labelFor = fill =>
    contrastRatio(c.ink, fill) >= contrastRatio(c.surface, fill) ? c.ink : c.surface;
  return {
    canvas: c.canvas,
    surface: c.surface,
    ink: c.ink,
    inkMuted: c.ink,
    line: c.ink,
    accent: c.accent,
    onAccent: labelFor(c.accent),
    accentStrong: c.accent,
    accentSoft: c.surface,
    accentInk: c.accent,
    band: c.band,
    onBand: labelFor(c.band),
  };
}

/**
 * CSS custom properties that re-theme the design-system utilities inside a
 * site root to the organization's palette. The palette is content (chosen by
 * the org, stored as data), applied at runtime; anything that is not a
 * #RRGGBB value is dropped, so a bad record falls back to the theme.
 */
export function paletteVars(tokens) {
  const t = tokens || {};
  const map = {
    '--z-canvas': t.canvas,
    '--z-surface': t.surface,
    '--z-raised': t.surface,
    '--z-sunken': t.canvas,
    '--z-line': t.line,
    '--z-accent': t.accent,
    '--z-on-accent': t.onAccent,
    '--z-accent-strong': t.accentStrong,
    '--z-accent-soft': t.accentSoft,
    '--z-accent-ink': t.accentInk,
    '--canvas': t.canvas,
    '--surface': t.surface,
    '--line': t.line,
    '--line-strong': t.inkMuted,
    '--ink': t.ink,
    '--ink-body': t.ink,
    '--ink-muted': t.inkMuted,
    '--ink-subtle': t.inkMuted,
    '--brand': t.accentInk,
    '--link': t.accentInk,
    '--deep': t.band,
    '--on-deep': t.onBand,
    '--on-deep-muted': t.onBand,
  };
  return Object.fromEntries(Object.entries(map).filter(([, v]) => isHex(v)));
}

/** Enabled pages in navigation order, home first. */
export function visiblePages(pages) {
  const p = pages || {};
  // The public projection carries enabled pages only (no `enabled` field);
  // the editor's copy carries every page with its flag. Home is always on.
  return PAGE_ORDER.filter(id => p[id] && (id === 'home' || p[id].enabled !== false));
}

/** The page a /s/:slug/:page URL names, or null when it is not a visible page. */
export function resolvePageId(pages, pageParam) {
  const id = pageParam || 'home';
  return visiblePages(pages).includes(id) ? id : null;
}

/**
 * Path of a page in the public renderer: `/s/<slug>/<page>` on the platform,
 * or `/<page>` when `basePath` is '' (the org's own website host, D60 item 4).
 */
export function pageHref(slug, pageId, basePath) {
  const base = basePath === undefined ? `/s/${encodeURIComponent(slug)}` : basePath;
  if (!pageId || pageId === 'home') return base || '/';
  return `${base}/${pageId}`;
}

/** Split block text into paragraphs on blank lines. */
export function paragraphs(body) {
  return String(body || '')
    .split(/\n\s*\n/)
    .map(p => p.trim())
    .filter(Boolean);
}

/** Mirror of the server's link rule: https or mailto only. */
export function isSafeLink(url) {
  if (typeof url !== 'string') return false;
  const value = url.trim();
  if (/^mailto:[^\s@]+@[^\s@]+\.[^\s@]+$/i.test(value)) return true;
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
}

/** Upcoming events only, soonest first, at most `limit`. */
export function upcomingEvents(events, now = Date.now(), limit = 6) {
  return (events || [])
    .filter(e => e && e.startsAt && Date.parse(e.endsAt || e.startsAt) >= now)
    .sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt))
    .slice(0, limit);
}

const STATUS = {
  pending: { label: 'Waiting for upload', tone: 'neutral' },
  scanning: { label: 'Scanning', tone: 'info' },
  passed: { label: 'Passed', tone: 'success' },
  rejected: { label: 'Rejected', tone: 'danger' },
};

/** Label and pill tone for an upload or logo scan status. */
export function scanStatus(status) {
  return STATUS[status] || { label: status || 'Unknown', tone: 'neutral' };
}

/** True while anything still needs polling. */
export function needsPolling(uploads, logoPending) {
  return (
    (uploads || []).some(u => u.status === 'scanning') ||
    (logoPending ? logoPending.status === 'scanning' : false)
  );
}

export function formatBytes(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** Client-side check before asking for an upload URL; the server re-checks. */
export function uploadFileError(file, limits) {
  if (!file) return 'Choose a .zip file';
  if (!/\.zip$/i.test(file.name)) return 'The site must be a .zip file';
  const max = limits?.maxArchiveBytes;
  if (max && file.size > max)
    return `The zip is ${formatBytes(file.size)}; the limit is ${formatBytes(max)}`;
  if (!file.size) return 'The zip is empty';
  return null;
}

export function logoFileError(file, limits) {
  if (!file) return 'Choose an image';
  const types = limits?.contentTypes || ['image/png', 'image/jpeg', 'image/webp'];
  if (!types.includes(file.type)) return 'The logo must be a PNG, JPEG or WebP image';
  if (limits?.maxBytes && file.size > limits.maxBytes) {
    return `The logo is ${formatBytes(file.size)}; the limit is ${formatBytes(limits.maxBytes)}`;
  }
  return null;
}

/** An editable copy of the stored pages, every page present. */
export function editablePages(pages) {
  const out = {};
  for (const id of PAGE_ORDER) {
    const page = (pages && pages[id]) || {};
    out[id] = {
      enabled: id === 'home' ? true : page.enabled !== false,
      title: page.title || '',
      blocks: (page.blocks || []).map(b => ({
        heading: b.heading || '',
        body: b.body || '',
        linkLabel: b.linkLabel || '',
        linkUrl: b.linkUrl || '',
      })),
    };
  }
  return out;
}

/** The PUT body for pages: empty link fields dropped, so the server does not reject them. */
export function pagesPayload(pages) {
  const out = {};
  for (const id of PAGE_ORDER) {
    const page = pages[id];
    if (!page) continue;
    out[id] = {
      enabled: page.enabled,
      title: page.title,
      blocks: page.blocks.map(b => {
        const block = { heading: b.heading, body: b.body };
        if (b.linkLabel || b.linkUrl) {
          block.linkLabel = b.linkLabel;
          block.linkUrl = b.linkUrl;
        }
        return block;
      }),
    };
  }
  return out;
}

/**
 * Custom domains (docs/Decisions.md D45): label, tone and next step for a
 * domain's status, mirroring src/services/sites/customDomains.js.
 */
const DOMAIN_STATUS = {
  'pending-verification': {
    label: 'Waiting for verification',
    tone: 'info',
    next: 'Add the TXT record below at your DNS provider, then choose Verify.',
    action: 'verify',
  },
  provisioning: {
    label: 'Setting up',
    tone: 'info',
    next: 'Point the domain at the CNAME target below. The certificate is issued once DNS points here; choose Check status after a few minutes.',
    action: 'refresh',
  },
  active: { label: 'Live', tone: 'success', next: null, action: 'refresh' },
  failed: {
    label: 'Certificate failed',
    tone: 'danger',
    next: 'Remove the domain and add it again. Check that nothing else (for example a CAA record) blocks Amazon from issuing a certificate.',
    action: null,
  },
  removing: {
    label: 'Removing',
    tone: 'neutral',
    next: 'The domain no longer shows your site. Choose Remove again in a few minutes to finish.',
    action: null,
  },
};

export function domainStatus(status) {
  return (
    DOMAIN_STATUS[status] || {
      label: status || 'Unknown',
      tone: 'neutral',
      next: null,
      action: null,
    }
  );
}

/** Client-side shape check only; the server is the authority. */
export function customDomainError(input) {
  const domain = String(input || '')
    .trim()
    .toLowerCase()
    .replace(/\.$/, '');
  if (!domain) return 'Enter a domain, for example www.example.org';
  if (
    domain.includes('://') ||
    domain.includes('/') ||
    !/^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/.test(domain)
  ) {
    return 'Enter a domain name without https:// or a path, for example www.example.org';
  }
  return null;
}
